import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommandeService } from 'src/app/core/services/commande.service';
import { UtilisateurService } from 'src/app/core/services/utilisateur.service';
import { EChartsOption } from 'echarts';

@Component({
  selector: 'app-client-detail',
  templateUrl: './client-detail.component.html',
  styleUrls: ['./client-detail.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ClientDetailComponent implements OnInit {
  clientId!: number;
  client: any = null;
  commandes: any[] = [];
  commandesFiltrees: any[] = [];
  chargement = true;

  statutFiltre = '';
  dateDebut = '';
  dateFin = '';

  chartActivite: EChartsOption = {};
  ongletActif: 'historique' | 'stats' = 'historique';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private commandeService: CommandeService,
    private utilisateurService: UtilisateurService
  ) {}

  ngOnInit(): void {
    this.clientId = Number(this.route.snapshot.paramMap.get('id'));
    this.chargerClient();
    this.chargerCommandes();
  }

  chargerClient(): void {
    this.utilisateurService.obtenirUtilisateurParId(this.clientId).subscribe({
      next: (u) => this.client = u
    });
  }

  chargerCommandes(): void {
    this.chargement = true;
    this.commandeService.obtenirCommandesParClient(this.clientId, 0, 200).subscribe({
      next: (res: any) => {
        this.commandes = res?.content || [];
        this.appliquerFiltres();
        this.construireChartActivite();
        this.chargement = false;
      },
      error: () => {
        this.commandes = [];
        this.commandesFiltrees = [];
        this.chargement = false;
      }
    });
  }

  private construireChartActivite(): void {
    const par6Mois: Record<string, number> = {};
    const maintenant = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(maintenant.getFullYear(), maintenant.getMonth() - i, 1);
      const cle = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      par6Mois[cle] = 0;
    }
    this.commandes.forEach(c => {
      if (!c.dateCreation) return;
      const d = new Date(c.dateCreation);
      const cle = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (par6Mois[cle] !== undefined) par6Mois[cle]++;
    });
    const labels = Object.keys(par6Mois).map(k => {
      const [y, m] = k.split('-');
      return new Date(+y, +m - 1, 1).toLocaleDateString('fr-FR', { month: 'short' });
    });
    this.chartActivite = {
      tooltip: { trigger: 'axis' },
      grid: { left: 16, right: 16, top: 10, bottom: 24, containLabel: true },
      xAxis: { type: 'category', data: labels, axisLabel: { fontSize: 11 } },
      yAxis: { type: 'value', minInterval: 1, axisLabel: { fontSize: 11 } },
      series: [{
        type: 'bar', data: Object.values(par6Mois),
        barMaxWidth: 36,
        itemStyle: { color: '#6366f1', borderRadius: [4, 4, 0, 0] }
      }]
    };
  }

  appliquerFiltres(): void {
    let data = [...this.commandes];
    if (this.statutFiltre) {
      data = data.filter(c => (c.statut || '').toString().toUpperCase() === this.statutFiltre.toUpperCase());
    }
    if (this.dateDebut) {
      const d = new Date(this.dateDebut);
      data = data.filter(c => !!c.dateCreation && new Date(c.dateCreation) >= d);
    }
    if (this.dateFin) {
      const d = new Date(this.dateFin);
      d.setHours(23, 59, 59, 999);
      data = data.filter(c => !!c.dateCreation && new Date(c.dateCreation) <= d);
    }
    this.commandesFiltrees = data;
  }

  get montantGenere(): number {
    return this.commandesFiltrees.reduce((acc, c) => acc + Number(c?.montantAPayer || 0), 0);
  }

  get tauxAcceptation(): number {
    if (!this.commandesFiltrees.length) return 0;
    const acceptees = this.commandesFiltrees.filter(c => !['ANNULE', 'ANNULEE'].includes((c?.statut || '').toString().toUpperCase())).length;
    return (acceptees / this.commandesFiltrees.length) * 100;
  }

  get delaiMoyenHeures(): number {
    const delais = this.commandesFiltrees
      .filter(c => c?.dateCreation && c?.dateLivraison)
      .map(c => (new Date(c.dateLivraison).getTime() - new Date(c.dateCreation).getTime()) / (1000 * 60 * 60))
      .filter(v => Number.isFinite(v) && v >= 0);
    if (!delais.length) return 0;
    return delais.reduce((a, b) => a + b, 0) / delais.length;
  }

  repartitionType(type: string): number {
    return this.commandesFiltrees.filter(c => (c?.typeEnvoi || '').toString() === type).length;
  }

  labelType(type: string): string {
    const map: Record<string, string> = {
      'EXPRESS_URBAIN':       'Express · Urbain',
      'STANDARD_URBAIN':      'Standard · Urbain',
      'EXPRESS_INTERURBAIN':  'Express · Interurbain',
      'STANDARD_INTERURBAIN': 'Standard · Interurbain',
    };
    return map[type] || type || '—';
  }

  classPill(type: string): string {
    if ((type || '').includes('EXPRESS'))     return 'crm-type-pill crm-type-pill--express';
    if ((type || '').includes('STANDARD'))    return 'crm-type-pill crm-type-pill--standard';
    return 'crm-type-pill';
  }

  nouvelleCommandeUrbaine(): void {
    this.router.navigate(['/commande/enregistrer'], { queryParams: { mode: 'urbain', clientId: this.clientId } });
  }

  nouvelleCommandeInterurbaine(): void {
    this.router.navigate(['/commande/enregistrer'], { queryParams: { mode: 'interurbain', clientId: this.clientId } });
  }

  get segmentClient(): { label: string; couleur: string } {
    const total = this.commandes.length;
    const ca = this.montantGenere;
    if (total >= 50 || ca >= 500000) return { label: 'VIP', couleur: 'gold' };
    if (total >= 15 || ca >= 150000) return { label: 'Fidèle', couleur: 'blue' };
    if (total >= 5)  return { label: 'Régulier', couleur: 'teal' };
    return { label: 'Occasionnel', couleur: 'grey' };
  }

  get scoreFidelite(): number {
    const total = this.commandes.length;
    const ca = this.montantGenere;
    const score = Math.min(100, Math.round((total * 2) + (ca / 10000)));
    return score;
  }

  get derniereCommande(): any {
    if (!this.commandes.length) return null;
    return this.commandes.reduce((a, b) =>
      new Date(a.dateCreation) > new Date(b.dateCreation) ? a : b
    );
  }

  get joursDepuisLastCmd(): number {
    const d = this.derniereCommande;
    if (!d?.dateCreation) return 0;
    return Math.floor((Date.now() - new Date(d.dateCreation).getTime()) / (1000 * 60 * 60 * 24));
  }

  formatDate(d: string): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  initiales(nom: string, prenom: string): string {
    return ((nom || '')[0] || '') + ((prenom || '')[0] || '');
  }
}
